import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  USER_ROLE_ADMIN,
  USER_ROLE_APARTMENT_LOCATOR,
  hasRoleAdmin,
} from 'common/userRoles';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import {
  placeholderProfilePicture,
  APT_LEADERBOARD_CUT_OFF_DATE,
} from 'common/apartmentLocatorHelpers';

import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import SrAgentLeaderboardBarChart from 'client/components/SrAgentLeaderboardBarChart';
import SrAgentLeaderboardCell from 'client/components/SrAgentLeaderboardCell';
import SrMonthlyFlicker from 'client/components/SrMonthlyFlicker';

import { SCREEN_ID_APARTMENT_LOCATOR_LEADERBOARD } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_APARTMENT_LOCATOR],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_LEADERBOARD)
@branch({
  currentUser: ['currentUser'],
  leaderboard: ['apartmentLocator', 'leaderboard'],
})
class ApartmentLocatorLeaderboardPage extends Component {
  static fetchData(match, inject) {
    const startDate = momentWithAustinTimezone().startOf('month').toISOString();
    const endDate = momentWithAustinTimezone().endOf('month').toISOString();
    return ApartmentLocatorActions.getLeaderboard(inject, { startDate, endDate });
  }

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    leaderboard: PropTypes.array.isRequired,
  }

  _onMonthUpdated = (startDate, endDate) => {
    const { dispatch } = this.props;
    dispatch(ApartmentLocatorActions.getLeaderboard, {
      startDate,
      endDate,
    });
  }

  render() {
    const { currentUser, leaderboard } = this.props;
    if (!leaderboard || leaderboard.length < 3) {
      return null;
    }

    const leaderboardUsers = hasRoleAdmin(currentUser.roles)
      ? leaderboard
      : leaderboard.slice(0, 5);

    const leaderboardTotalLeasesSigned = leaderboardUsers
      .reduce((acc, user) => acc + user.leaseRateScore.signedLeadCount, 0);

    return (
      <div className='page-agent-leaderboard'>
        <Navbar
          redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })}
          leftButton={(
            <SrLink
              className='link back'
              redirectToLastLocation
              restoreScrollPosition
              to='/agent/locator-profile'
            >
              Back
            </SrLink>
          )}
        />
        <SrMonthlyFlicker
          title='Leaderboard'
          onMonthUpdated={(startDate, endDate) => this._onMonthUpdated(startDate, endDate)}
          limitDate={APT_LEADERBOARD_CUT_OFF_DATE}
        />
        {hasRoleAdmin(currentUser.roles) && (
          <div className='leaderboardTotal'>
            {`Total Leases Signed: ${leaderboardTotalLeasesSigned}`}
          </div>
        )}
        <div className='barChartContainer'>
          <SrAgentLeaderboardBarChart
            firstPlaceProfilePictureUrl={
              leaderboardUsers[0].locator.profilePicture || placeholderProfilePicture
            }
            secondPlaceProfilePictureUrl={
              leaderboardUsers[1].locator.profilePicture || placeholderProfilePicture
            }
            thirdPlaceProfilePictureUrl={
              leaderboardUsers[2].locator.profilePicture || placeholderProfilePicture
            }
          />
        </div>
        <div className='leaderboardSpacing' />
        <div className='leaderboardContainer'>
          {leaderboardUsers.map((row, index) => (
            <SrAgentLeaderboardCell
              key={row.locator.id}
              rank={index + 1}
              profilePictureUrl={row.locator.profilePicture || placeholderProfilePicture}
              agentName={row.locator.name}
              leaseVolume={row.leaseRateScore.signedLeadCount}
            />
          ))}
        </div>
      </div>
    );
  }
}

ApartmentLocatorLeaderboardPage.displayName = 'ApartmentLocatorLeaderboardPage';

export default ApartmentLocatorLeaderboardPage;
